import React, { useState } from 'react';

// AI Immigration Expert - Step 6: Document Preparation & Labeling
export const AIExpertStep6DocumentPrep = ({ formData, setFormData, onNext, onBack }) => {
  const [labeling, setLabeling] = useState(false);
  const [documentLabels, setDocumentLabels] = useState(formData.documentLabels || []);
  const [templates, setTemplates] = useState(formData.templates || {});

  const performDocumentLabeling = async () => {
    setLabeling(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate AI document labeling
      const allDocuments = [
        ...(formData.uploadedFiles || []),
        ...(formData.additionalUploads || [])
      ];

      const labeledDocs = allDocuments.map((doc, index) => ({
        ...doc,
        exhibit: `Exhibit ${String.fromCharCode(65 + index)}`, // A, B, C, etc.
        description: `${doc.name} - ${doc.category || 'Supporting Evidence'}`,
        pages: Math.ceil(doc.size / 50000), // Estimate pages
        category: doc.category || 'Supporting Evidence'
      }));

      const generatedTemplates = {
        coverLetter: generateCoverLetter(formData),
        exhibitList: generateExhibitList(labeledDocs),
        affidavitTemplate: generateAffidavitTemplate(formData),
        personalDeclaration: generatePersonalDeclaration(formData)
      };

      setDocumentLabels(labeledDocs);
      setTemplates(generatedTemplates);
      setFormData({ 
        ...formData, 
        documentLabels: labeledDocs, 
        templates: generatedTemplates 
      });
    } catch (error) {
      console.error('Document labeling error:', error);
    }
    
    setLabeling(false);
  };

  const generateCoverLetter = (data) => {
    const caseType = data.caseType || 'Immigration Application';
    const applicantName = data.applicantName || 'Applicant';
    
    return `Dear USCIS Officer,

Re: ${caseType} for ${applicantName}

I am submitting this application package with the following supporting evidence organized by exhibit. Each document has been carefully reviewed and labeled for your convenience.

This package includes:
- Primary application forms and supporting evidence
- Financial documentation demonstrating eligibility
- Character references and affidavits
- Medical and background clearances as required

Please find the complete exhibit list attached with page references for easy navigation.

Thank you for your consideration of this application.

Respectfully submitted,
${applicantName}`;
  };

  const generateExhibitList = (docs) => {
    return docs.map(doc => `${doc.exhibit}: ${doc.description} (${doc.pages} pages)`).join('\n');
  };

  const generateAffidavitTemplate = (data) => {
    return `AFFIDAVIT OF SUPPORT

I, [NAME], being duly sworn, hereby declare:

1. I am a [US Citizen/Permanent Resident] and [relationship to applicant].

2. I have personal knowledge of the facts stated herein.

3. [Specific details about relationship, support, or circumstances]

4. I am willing to provide additional information if required.

I declare under penalty of perjury that the foregoing is true and correct.

_______________________     Date: __________
[Signature]

[Name]
[Address]
[Phone Number]

Subscribed and sworn to before me this ___ day of _______, 20__.

_______________________
Notary Public`;
  };

  const generatePersonalDeclaration = (data) => {
    return `PERSONAL DECLARATION

I, [APPLICANT NAME], declare:

1. I am the applicant in this matter.

2. All statements made in this application are true and correct to the best of my knowledge.

3. [Specific declaration points relevant to the case]

4. I understand that any false statements may result in denial of my application.

_______________________     Date: __________
[Signature]

[Printed Name]`;
  };

  React.useEffect(() => {
    if (documentLabels.length === 0) {
      performDocumentLabeling();
    }
  }, []);

  const downloadTemplate = (templateName, content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateName.replace(/([A-Z])/g, '_$1').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📋 Document Preparation & Labeling</h3>
        <p className="text-gray-400 text-lg">AI-organized exhibits and professional templates</p>
      </div>

      {labeling ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Labeling Documents</h4>
          <p className="text-gray-400">AI is organizing and labeling your evidence...</p>
        </div>
      ) : (
        <>
          {/* Document Labels */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📁 Organized Evidence List</h4>
            <div className="space-y-3">
              {(documentLabels || []).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-blue-400 font-bold mr-4 min-w-[80px]">{doc.exhibit}</span>
                    <div>
                      <p className="text-white font-medium">{doc.name}</p>
                      <p className="text-gray-400 text-sm">{doc.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-300">{doc.pages} pages</p>
                    <p className="text-gray-400 text-sm">{doc.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table of Contents Preview */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📖 Table of Contents / Exhibit List</h4>
            <div className="bg-gray-700 p-4 rounded-lg font-mono text-sm">
              <pre className="text-gray-300 whitespace-pre-wrap">{templates.exhibitList}</pre>
            </div>
            <button
              onClick={() => downloadTemplate('exhibit_list', templates.exhibitList)}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              📥 Download Exhibit List
            </button>
          </div>

          {/* Templates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cover Letter Template */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📄 Cover Letter Template</h4>
              <div className="bg-gray-700 p-4 rounded-lg text-sm max-h-40 overflow-y-auto">
                <pre className="text-gray-300 whitespace-pre-wrap">{templates.coverLetter}</pre>
              </div>
              <button
                onClick={() => downloadTemplate('cover_letter', templates.coverLetter)}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                📥 Download Template
              </button>
            </div>

            {/* Affidavit Template */}
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Affidavit Template</h4>
              <div className="bg-gray-700 p-4 rounded-lg text-sm max-h-40 overflow-y-auto">
                <pre className="text-gray-300 whitespace-pre-wrap">{templates.affidavitTemplate}</pre>
              </div>
              <button
                onClick={() => downloadTemplate('affidavit_template', templates.affidavitTemplate)}
                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                📥 Download Template
              </button>
            </div>
          </div>

          {/* Organization Summary */}
          <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">✨ Organization Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{documentLabels.length}</div>
                <p className="text-gray-400">Documents Labeled</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {documentLabels.reduce((sum, doc) => sum + doc.pages, 0)}
                </div>
                <p className="text-gray-400">Total Pages</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">4</div>
                <p className="text-gray-400">Templates Generated</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {new Set(documentLabels.map(doc => doc.category)).size}
                </div>
                <p className="text-gray-400">Evidence Categories</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={labeling}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !labeling
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Final Scoring →
        </button>
      </div>
    </div>
  );
};

// AI Immigration Expert - Step 7: Final Case Scoring
export const AIExpertStep7FinalScoring = ({ formData, setFormData, onNext, onBack }) => {
  const [calculating, setCalculating] = useState(false);
  const [finalScores, setFinalScores] = useState(formData.finalScores || null);

  const calculateFinalScores = async () => {
    setCalculating(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Calculate scores based on actual accumulated data
      const analysisResults = formData.analysisResults || {};
      const riskResults = formData.riskResults || {};
      const selectedActions = formData.selectedActions || [];
      const documentLabels = formData.documentLabels || [];
      const uploadedFiles = formData.uploadedFiles || [];
      
      // Comprehensive scoring based on totality of case
      const evidenceScore = calculateEvidenceStrength(analysisResults, uploadedFiles);
      const riskScore = calculateRiskAdjustment(riskResults);
      const complianceScore = calculateComplianceScore(riskResults, analysisResults);
      const improvementScore = calculateImprovementBonus(selectedActions, analysisResults);
      const organizationScore = calculateOrganizationScore(documentLabels, uploadedFiles);
      
      const overallReadiness = calculateOverallReadiness(
        evidenceScore, 
        riskScore, 
        complianceScore, 
        improvementScore, 
        organizationScore
      );
      
      const scores = {
        evidenceStrength: evidenceScore,
        riskLevel: getRiskLevel(riskScore),
        riskScore: riskScore,
        complianceScore: complianceScore,
        overallReadiness: overallReadiness,
        recommendations: generateScoreRecommendations(overallReadiness, evidenceScore, riskScore),
        breakdown: {
          documentQuality: calculateDocumentQuality(analysisResults),
          evidenceGaps: calculateEvidenceGaps(analysisResults),
          riskMitigation: calculateRiskMitigation(riskResults),
          organization: organizationScore,
          improvementActions: improvementScore
        },
        detailed_analysis: {
          total_documents: uploadedFiles.length,
          categories_covered: getCategoriesCovered(analysisResults),
          high_risk_items: getHighRiskItems(riskResults),
          critical_missing: getCriticalMissing(analysisResults),
          case_complexity: calculateCaseComplexity(analysisResults, riskResults)
        }
      };

      setFinalScores(scores);
      setFormData({ ...formData, finalScores: scores });
    } catch (error) {
      console.error('Score calculation error:', error);
    }
    
    setCalculating(false);
  };

  // Comprehensive scoring functions
  const calculateEvidenceStrength = (analysis, files) => {
    let score = 0;
    
    // Base score from document count (0-25 points)
    score += Math.min(files.length * 3, 25);
    
    // Category strength scoring (0-40 points)
    const categories = analysis.evidence_categories || {};
    Object.values(categories).forEach(cat => {
      if (cat.rating === '✅') score += 8;
      else if (cat.rating === '⚠️') score += 4;
      // Missing categories get 0 points
    });
    
    // Quality assessment (0-25 points)
    const quality = analysis.quality_assessment || {};
    const avgQuality = (quality.readability + quality.completeness + quality.formatting + quality.consistency) / 4;
    score += (avgQuality / 100) * 25;
    
    // Penalty for missing essentials (up to -10 points)
    const missingCount = (analysis.missing_essentials || []).length;
    score -= Math.min(missingCount * 2, 10);
    
    return Math.max(0, Math.min(100, Math.round(score)));
  };

  const calculateRiskAdjustment = (risks) => {
    let riskScore = 0;
    
    // Base risk from overall assessment
    const overallRisk = risks.overall_risk_level || 'Medium';
    if (overallRisk === 'High') riskScore += 40;
    else if (overallRisk === 'Medium') riskScore += 20;
    else riskScore += 5;
    
    // Additional risk from detailed risks
    const detailedRisks = risks.detailed_risks || [];
    detailedRisks.forEach(risk => {
      if (risk.severity === 'High') riskScore += 15;
      else if (risk.severity === 'Medium') riskScore += 8;
      else riskScore += 3;
    });
    
    // Consistency check failures
    const consistency = risks.consistency_checks || {};
    Object.values(consistency).forEach(check => {
      if (check.status === 'Warning') riskScore += 5;
      else if (check.status === 'Fail') riskScore += 10;
    });
    
    return Math.min(100, riskScore);
  };

  const calculateComplianceScore = (risks, analysis) => {
    let score = 100;
    
    // USCIS compliance deductions
    const compliance = risks.compliance_check || {};
    if (compliance.uscis_rules !== 'Compliant') score -= 15;
    if (compliance.filing_deadlines === 'Behind Schedule') score -= 10;
    if (compliance.form_versions !== 'Current') score -= 5;
    
    // Document quality compliance
    const quality = analysis.quality_assessment || {};
    if (quality.formatting < 70) score -= 10;
    if (quality.readability < 70) score -= 10;
    if (quality.completeness < 50) score -= 15;
    
    return Math.max(0, score);
  };

  const calculateImprovementBonus = (selectedActions, analysis) => {
    let bonus = 0;
    
    // Bonus for selecting improvement actions
    bonus += selectedActions.length * 2;
    
    // Extra bonus for addressing high-priority items
    const highPrioritySelected = selectedActions.filter(actionId => 
      (formData.recommendations || []).some(rec => 
        rec.id === actionId && rec.priority === 'High'
      )
    ).length;
    
    bonus += highPrioritySelected * 5;
    
    return Math.min(20, bonus);
  };

  const calculateOrganizationScore = (labels, files) => {
    if (files.length === 0) return 0;
    
    let score = 60; // Base organization score
    
    // Bonus for document labeling
    if (labels.length > 0) score += 20;
    
    // Bonus for comprehensive organization
    if (labels.length === files.length) score += 15;
    
    // File naming and organization bonus
    const wellNamedFiles = files.filter(file => 
      file.name.length > 10 && !file.name.includes('untitled')
    ).length;
    
    score += (wellNamedFiles / files.length) * 5;
    
    return Math.min(100, Math.round(score));
  };

  const calculateOverallReadiness = (evidence, risk, compliance, improvement, organization) => {
    // Weighted calculation considering all factors
    const weights = {
      evidence: 0.35,      // 35% - most important
      compliance: 0.25,    // 25% - critical for acceptance
      risk: -0.20,         // -20% - negative impact
      organization: 0.10,  // 10% - presentation matters
      improvement: 0.10    // 10% - proactive improvements
    };
    
    const weightedScore = 
      (evidence * weights.evidence) +
      (compliance * weights.compliance) +
      (risk * weights.risk) +  // Risk reduces score
      (organization * weights.organization) +
      (improvement * weights.improvement);
    
    return Math.max(0, Math.min(100, Math.round(weightedScore)));
  };

  const getRiskLevel = (riskScore) => {
    if (riskScore >= 60) return 'High';
    if (riskScore >= 30) return 'Medium';
    return 'Low';
  };

  const generateScoreRecommendations = (overall, evidence, risk) => {
    return {
      readyToSubmit: overall >= 80 && evidence >= 75 && risk < 30,
      needsImprovement: overall >= 60 && overall < 80,
      majorConcerns: overall < 60 || evidence < 50 || risk >= 60
    };
  };

  const calculateDocumentQuality = (analysis) => {
    const quality = analysis.quality_assessment || {};
    return Math.round((quality.readability + quality.completeness + quality.formatting + quality.consistency) / 4);
  };

  const calculateEvidenceGaps = (analysis) => {
    const missing = (analysis.missing_essentials || []).length;
    return Math.max(0, 100 - (missing * 15));
  };

  const calculateRiskMitigation = (risks) => {
    const highRisks = (risks.detailed_risks || []).filter(r => r.severity === 'High').length;
    return Math.max(0, 100 - (highRisks * 25));
  };

  const getCategoriesCovered = (analysis) => {
    const categories = analysis.evidence_categories || {};
    return Object.values(categories).filter(cat => cat.rating !== '❌').length;
  };

  const getHighRiskItems = (risks) => {
    return (risks.detailed_risks || []).filter(r => r.severity === 'High').length;
  };

  const getCriticalMissing = (analysis) => {
    return (analysis.missing_essentials || []).length;
  };

  const calculateCaseComplexity = (analysis, risks) => {
    let complexity = 'Simple';
    
    const missingCount = (analysis.missing_essentials || []).length;
    const riskCount = (risks.detailed_risks || []).length;
    const overallStrength = analysis.overall_strength || 100;
    
    if (missingCount > 3 || riskCount > 2 || overallStrength < 50) {
      complexity = 'Complex';
    } else if (missingCount > 1 || riskCount > 0 || overallStrength < 70) {
      complexity = 'Moderate';
    }
    
    return complexity;
  };

  React.useEffect(() => {
    if (!finalScores) {
      calculateFinalScores();
    }
  }, []);

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (score) => {
    if (score >= 85) return 'bg-green-900/20 border-green-500';
    if (score >= 70) return 'bg-yellow-900/20 border-yellow-500';
    return 'bg-red-900/20 border-red-500';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Low': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRecommendationText = () => {
    if (!finalScores) return '';
    
    if (finalScores.recommendations.readyToSubmit) {
      return 'Your case appears ready for submission with strong evidence and low risk factors.';
    } else if (finalScores.recommendations.needsImprovement) {
      return 'Consider addressing the identified issues before submission to strengthen your case.';
    } else {
      return 'Significant improvements recommended before submission. Consider legal consultation.';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📊 Final Case Scoring</h3>
        <p className="text-gray-400 text-lg">Comprehensive assessment of your application readiness</p>
      </div>

      {calculating ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-pulse text-6xl mb-4">🧮</div>
          <h4 className="text-xl text-white mb-2">Calculating Final Scores</h4>
          <p className="text-gray-400">Analyzing all factors to determine case strength...</p>
        </div>
      ) : finalScores && (
        <>
          {/* Overall Readiness Score */}
          <div className={`p-6 rounded-lg border-2 text-center ${getScoreBgColor(finalScores.overallReadiness)}`}>
            <h4 className="text-xl text-white mb-2">🎯 Overall Case Readiness</h4>
            <div className={`text-6xl font-bold mb-2 ${getScoreColor(finalScores.overallReadiness)}`}>
              {finalScores.overallReadiness}%
            </div>
            <p className="text-gray-400">{getRecommendationText()}</p>
          </div>

          {/* Detailed Scores */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-white mb-3">📈 Evidence Strength</h4>
              <div className={`text-4xl font-bold mb-2 ${getScoreColor(finalScores.evidenceStrength)}`}>
                {finalScores.evidenceStrength}%
              </div>
              <p className="text-gray-400">Quality and completeness of submitted evidence</p>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-white mb-3">⚠️ Risk Assessment</h4>
              <div className={`text-4xl font-bold mb-2 ${getRiskColor(finalScores.riskLevel)}`}>
                {finalScores.riskLevel}
              </div>
              <p className="text-gray-400">Potential issues and red flags identified</p>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg text-center">
              <h4 className="text-lg font-semibold text-white mb-3">✅ USCIS Compliance</h4>
              <div className={`text-4xl font-bold mb-2 ${getScoreColor(finalScores.complianceScore)}`}>
                {finalScores.complianceScore}%
              </div>
              <p className="text-gray-400">Adherence to USCIS requirements and standards</p>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 Detailed Score Breakdown</h4>
            <div className="space-y-4">
              {Object.entries(finalScores.breakdown).map(([category, score]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-gray-300 capitalize">{category.replace(/([A-Z])/g, ' $1')}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className={`h-2 rounded-full ${score >= 85 ? 'bg-green-500' : score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    <span className={`font-bold ${getScoreColor(score)}`}>{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🎯 Submission Recommendations</h4>
            <div className="space-y-3">
              {finalScores.recommendations.readyToSubmit && (
                <div className="flex items-center p-3 bg-green-900/20 border border-green-500 rounded-lg">
                  <span className="text-2xl mr-3">✅</span>
                  <div>
                    <p className="text-green-400 font-semibold">Ready for Submission</p>
                    <p className="text-gray-400 text-sm">Your case meets submission standards</p>
                  </div>
                </div>
              )}
              
              {finalScores.recommendations.needsImprovement && !finalScores.recommendations.readyToSubmit && (
                <div className="flex items-center p-3 bg-yellow-900/20 border border-yellow-500 rounded-lg">
                  <span className="text-2xl mr-3">⚠️</span>
                  <div>
                    <p className="text-yellow-400 font-semibold">Improvements Recommended</p>
                    <p className="text-gray-400 text-sm">Address identified issues to strengthen your case</p>
                  </div>
                </div>
              )}
              
              {finalScores.recommendations.majorConcerns && (
                <div className="flex items-center p-3 bg-red-900/20 border border-red-500 rounded-lg">
                  <span className="text-2xl mr-3">🚨</span>
                  <div>
                    <p className="text-red-400 font-semibold">Major Concerns Identified</p>
                    <p className="text-gray-400 text-sm">Consider legal consultation before submission</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={calculating}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !calculating
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Generate Final Package →
        </button>
      </div>
    </div>
  );
};